import { useState, useEffect, useRef } from 'react';
import { clinicasService, type ClinicaItem } from '../../services/clinicas.service';
import ClinicaSearch from './ClinicaSearch';
import {
  Building,
  Calendar,
  MapPin,
  Phone,
  Check,
  Edit3,
  AlertCircle,
  Clock,
  Loader2,
} from 'lucide-react';

interface EmployeeSchedulingData {
  id: string;
  nome: string;
  cargo?: string;
  clinica?: string;
  clinicaNome?: string;
  dataAgendamento?: string;
  modeloExame?: string;
  escolha_clinica_tipo?: 'rh' | 'colaborador';
  exame?: string;
  variacao?: string;
  examesSelecionados?: number[];
}

interface ColaboradorClinicaPanelProps {
  employee: EmployeeSchedulingData;
  onSelectClinica: (clinica: ClinicaItem) => void;
  onSelectDataAgendamento: (data: string) => void;
}

export default function ColaboradorClinicaPanel({
  employee,
  onSelectClinica,
  onSelectDataAgendamento,
}: ColaboradorClinicaPanelProps) {
  const [selectedClinica, setSelectedClinica] = useState<ClinicaItem | null>(() => {
    if (employee.clinica) {
      const id = parseInt(employee.clinica);
      if (!isNaN(id) && id > 0) {
        return {
          id,
          nome: employee.clinicaNome || 'Clínica Selecionada',
          cidade: '',
          uf: '',
        };
      }
    }
    return null;
  });

  const [isEditingClinica, setIsEditingClinica] = useState(false);
  const [datasDisponiveis, setDatasDisponiveis] = useState<string[]>(() => {
    if (employee.dataAgendamento) {
      return [employee.dataAgendamento];
    }
    return [];
  });
  const [loadingClinica, setLoadingClinica] = useState(false);
  const [loadingDatas, setLoadingDatas] = useState(false);

  const selectedClinicaIdRef = useRef<number | null>(
    employee.clinica ? parseInt(employee.clinica) || null : null
  );

  // Sincronizar selectedClinica caso employee.clinica mude externamente
  useEffect(() => {
    if (employee.clinica) {
      const clinicaId = parseInt(employee.clinica);
      if (!isNaN(clinicaId) && clinicaId > 0 && selectedClinicaIdRef.current !== clinicaId) {
        selectedClinicaIdRef.current = clinicaId;
        setSelectedClinica((prev) => {
          if (prev && prev.id === clinicaId) return prev;
          return {
            id: clinicaId,
            nome: employee.clinicaNome || prev?.nome || 'Clínica Selecionada',
            cidade: prev?.cidade || '',
            uf: prev?.uf || '',
          };
        });
      }
    } else {
      setSelectedClinica(null);
      selectedClinicaIdRef.current = null;
    }
  }, [employee.clinica, employee.clinicaNome]);

  // Carregar dados completos da clínica (endereço, telefone, etc.) via API
  useEffect(() => {
    let isMounted = true;

    async function carregarClinicaInicial() {
      if (!employee.clinica) {
        return;
      }

      const clinicaId = parseInt(employee.clinica);
      if (isNaN(clinicaId) || clinicaId <= 0) {
        return;
      }

      try {
        setLoadingClinica(true);
        const data = await clinicasService.getClinicaById(clinicaId);
        if (isMounted && data) {
          setSelectedClinica((prev) => ({
            ...prev,
            ...data,
            nome: data.nome || prev?.nome || employee.clinicaNome || 'Clínica Selecionada',
          }));
          selectedClinicaIdRef.current = data.id;
          setIsEditingClinica(false);
        }
      } catch (err) {
        console.warn(`[ColaboradorClinicaPanel] Erro ao carregar detalhes da clínica ${clinicaId}:`, err);
        if (isMounted && employee.clinicaNome) {
          setSelectedClinica((prev) => prev || {
            id: clinicaId,
            nome: employee.clinicaNome!,
            cidade: '',
            uf: '',
          });
          selectedClinicaIdRef.current = clinicaId;
        }
      } finally {
        if (isMounted) setLoadingClinica(false);
      }
    }

    carregarClinicaInicial();

    return () => {
      isMounted = false;
    };
  }, [employee.clinica, employee.clinicaNome]);

  // Carregar datas disponíveis sempre que a clínica selecionada ou modelo de exame mudar
  useEffect(() => {
    let isMounted = true;

    async function carregarDatas() {
      if (!selectedClinica) {
        setDatasDisponiveis([]);
        return;
      }

      try {
        setLoadingDatas(true);
        const isOnline = employee.modeloExame === '1';
        const exameId = employee.exame ? parseInt(employee.exame, 10) : undefined;
        const clinicosIds = [
          ...(employee.variacao && employee.variacao !== '1' ? [parseInt(employee.variacao, 10)] : []),
          ...(employee.examesSelecionados || []),
        ];
        const datas = await clinicasService.getProximasDatasDisponiveis(
          selectedClinica.id,
          60,
          isOnline,
          exameId,
          clinicosIds,
        );
        if (isMounted) {
          let listaFinal = Array.isArray(datas) ? [...datas] : [];

          // Se o colaborador já possui uma data selecionada que não veio na lista (ex: rascunho anterior), preservar na lista
          if (employee.dataAgendamento) {
            let dataFormatada = employee.dataAgendamento.split('T')[0];
            if (dataFormatada.includes('-') && !dataFormatada.includes('/')) {
              const [ano, mes, dia] = dataFormatada.split('-');
              dataFormatada = `${dia}/${mes}/${ano}`;
            }

            if (!listaFinal.includes(dataFormatada) && !listaFinal.includes(employee.dataAgendamento)) {
              listaFinal = [dataFormatada, ...listaFinal];
            }
          }

          setDatasDisponiveis(listaFinal);
        }
      } catch (err) {
        console.warn('[ColaboradorClinicaPanel] Erro ao buscar datas disponíveis:', err);
        if (isMounted && employee.dataAgendamento) {
          setDatasDisponiveis([employee.dataAgendamento]);
        }
      } finally {
        if (isMounted) setLoadingDatas(false);
      }
    }

    carregarDatas();

    return () => {
      isMounted = false;
    };
  }, [selectedClinica, employee.modeloExame, employee.dataAgendamento, employee.exame, employee.examesSelecionados, employee.variacao]);

  const handleClinicaChosen = (clinica: ClinicaItem) => {
    setSelectedClinica(clinica);
    setIsEditingClinica(false);
    onSelectClinica(clinica);
  };

  const formatDateOption = (data: string) => {
    if (data.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [dia, mes, ano] = data.split('/');
      const dataObj = new Date(`${ano}-${mes}-${dia}T00:00:00`);
      return dataObj.toLocaleDateString('pt-BR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    try {
      return new Date(data).toLocaleDateString('pt-BR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return data;
    }
  };

  return (
    <div className="mt-3 p-4 sm:p-5 bg-gradient-to-b from-emerald-50/40 to-white rounded-2xl border-2 border-emerald-200/90 shadow-sm animate-in fade-in slide-in-from-top-2 duration-200 space-y-5">
      {/* Cabeçalho do Painel */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-emerald-100">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-2xs">
            <Building className="w-4 h-4" />
          </div>
          <div>
            <h5 className="font-bold text-sm text-gray-900 leading-tight">
              Configuração de Clínica e Data • RH Escolhe Agora
            </h5>
            <p className="text-[11px] text-gray-500">
              Selecione a clínica credenciada e a data de realização do exame para{' '}
              <strong className="text-gray-700">{employee.nome}</strong>.
            </p>
          </div>
        </div>

        {selectedClinica && !isEditingClinica && (
          <span className="self-start sm:self-auto text-[10px] uppercase font-extrabold tracking-wider bg-emerald-100 text-emerald-800 px-2.5 py-1 rounded-full border border-emerald-300 inline-flex items-center gap-1">
            <Check className="w-3 h-3" /> Clínica Definida
          </span>
        )}
      </div>

      {/* Loading inicial da clínica se estiver buscando */}
      {loadingClinica && (
        <div className="flex items-center justify-center py-6 gap-2 text-xs font-semibold text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
          <span>Carregando dados da clínica selecionada...</span>
        </div>
      )}

      {/* Exibição da Clínica: Card Selecionado ou Busca Completa */}
      {!loadingClinica && (
        <div>
          {selectedClinica && !isEditingClinica ? (
            /* Card da Clínica Selecionada */
            <div className="relative p-4 rounded-xl border-2 border-emerald-500 bg-emerald-50/80 shadow-xs transition-all">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex-1 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <h4 className="font-extrabold text-base text-emerald-950">
                      {selectedClinica.nome}
                    </h4>
                    <span className="bg-emerald-200 text-emerald-900 text-[10px] font-bold px-2 py-0.5 rounded-full">
                      Selecionada
                    </span>
                  </div>

                  <div className="space-y-1 text-xs text-gray-700">
                    <p className="flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 text-emerald-700 shrink-0" />
                      <span>
                        {selectedClinica.endereco}
                        {selectedClinica.numero ? `, ${selectedClinica.numero}` : ''}
                        {selectedClinica.complemento ? ` - ${selectedClinica.complemento}` : ''}
                        <br />
                        <span className="text-gray-500">
                          {selectedClinica.bairro ? `${selectedClinica.bairro}, ` : ''}
                          {selectedClinica.cidade} - {selectedClinica.uf}
                        </span>
                      </span>
                    </p>

                    {selectedClinica.telefones && selectedClinica.telefones.length > 0 && (
                      <p className="flex items-center gap-2 font-mono text-[11px] text-gray-600">
                        <Phone className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                        <span>{selectedClinica.telefones[0]}</span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-start shrink-0">
                  <button
                    type="button"
                    onClick={() => setIsEditingClinica(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-emerald-100 text-emerald-800 text-xs font-bold rounded-lg border border-emerald-300 transition-colors shadow-2xs"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    <span>Alterar Clínica</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Modo de Busca Completa de Clínica */
            <div className="space-y-3">
              {isEditingClinica && selectedClinica && (
                <div className="flex items-center justify-between pb-1">
                  <span className="text-xs font-semibold text-gray-600">
                    Alterando clínica para este colaborador:
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsEditingClinica(false)}
                    className="text-xs font-bold text-gray-500 hover:text-gray-800 underline"
                  >
                    Cancelar alteração
                  </button>
                </div>
              )}
              <ClinicaSearch
                onClinicaSelect={handleClinicaChosen}
                selectedClinica={selectedClinica}
                modeloExame={employee.modeloExame || '0'}
              />
            </div>
          )}
        </div>
      )}

      {/* Seleção de Data de Agendamento */}
      {selectedClinica && (
        <div className="pt-2 border-t border-emerald-100/80 space-y-2">
          <label className="block text-xs font-bold text-gray-800 uppercase tracking-wider">
            Data de Agendamento na Clínica *
          </label>

          {loadingDatas ? (
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200 text-xs text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
              <span>Consultando agenda e vagas disponíveis na clínica...</span>
            </div>
          ) : datasDisponiveis.length > 0 ? (
            <div className="space-y-1.5">
              <div className="relative">
                <select
                  value={employee.dataAgendamento || ''}
                  onChange={(e) => onSelectDataAgendamento(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-xs font-medium border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-gray-900 shadow-2xs"
                  required
                >
                  <option value="">Selecione uma data disponível na clínica...</option>
                  {datasDisponiveis.map((data) => (
                    <option key={data} value={data}>
                      {formatDateOption(data)}
                    </option>
                  ))}
                </select>
                <Calendar className="w-4 h-4 text-emerald-600 absolute left-3 top-3 pointer-events-none" />
              </div>
              <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-gray-400" />
                <span>
                  Próximas <strong>{datasDisponiveis.length}</strong> datas com horários disponíveis nesta clínica
                </span>
              </p>
            </div>
          ) : (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <strong>Nenhuma vaga imediata encontrada para esta clínica:</strong> Você pode selecionar outra clínica credenciada próxima ou entrar em contato para abertura de agenda extraordinária.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Card Informativo PGR / PCMSO */}
      <div className="p-3 bg-white border border-emerald-200/80 rounded-xl text-xs text-emerald-950 flex items-start gap-2.5 shadow-2xs">
        <span className="text-base leading-none">📋</span>
        <div className="leading-relaxed">
          <strong className="text-emerald-900 block font-bold mb-0.5">
            Exames Vinculados conforme PGR / PCMSO
          </strong>
          {employee.cargo ? (
            <span>
              Para o cargo <strong>{employee.cargo}</strong>, o Exame Clínico Ocupacional obrigatório e todos os exames complementares parametrizados no PGR/PCMSO da empresa serão incluídos automaticamente.
            </span>
          ) : (
            <span>
              O Exame Clínico Ocupacional obrigatório está incluído. Se houver exames complementares cadastrados no PGR/PCMSO da empresa para a função, eles serão anexados no pedido.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
